import { useEffect, useRef, useCallback } from "react";
import "./CSS_styles/about3d.css";

/*
  WebGL Fluid-Distortion Overlay
  ─────────────────────────────
  • A thick, golden/amber fluid layer covers the text.
  • Moving the cursor "pushes" the fluid away, revealing the text.
  • Idle droplets slowly re-merge and close the gap.
  • Small satellite droplets beside the cursor merge into the main clear zone.
  
  Technique: fullscreen <canvas> with a custom metaball / SDF shader.
  We track the cursor trail as a series of "clear points" that fade over time.
  The shader renders the fluid everywhere *except* near those points,
  creating the reveal effect.  Metaball math makes nearby points merge smoothly.
*/

const VERT = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAG = `
precision highp float;

uniform vec2  u_resolution;
uniform float u_time;
uniform vec2  u_points[64];
uniform float u_sizes[64];
uniform float u_strengths[64];
uniform int   u_count;
uniform float u_dpr;

// Simplex-ish noise for organic texture
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p *= 2.1;
    a *= 0.48;
  }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy;
  vec2 norm = gl_FragCoord.xy / u_resolution;

  // ── metaball field from cursor trail points ──
  float field = 0.0;
  for (int i = 0; i < 64; i++) {
    if (i >= u_count) break;
    vec2 p = u_points[i] * u_dpr;
    float sz = u_sizes[i] * u_dpr;
    float st = u_strengths[i];
    float d = length(uv - p);
    // Metaball contribution: smooth falloff
    field += st * (sz * sz) / (d * d + 1.0);
  }

  // field > threshold  →  "cleared" (text visible)
  // field < threshold  →  fluid covers
  float threshold = 1.0;
  float edge = smoothstep(threshold - 0.15, threshold + 0.35, field);

  // ── organic fluid texture ──
  float t = u_time * 0.12;
  vec2 st2 = norm * 3.2;
  float n1 = fbm(st2 + vec2(t, -t * 0.7));
  float n2 = fbm(st2 * 1.5 + vec2(-t * 0.5, t * 0.3) + n1 * 0.8);
  float pattern = mix(n1, n2, 0.5);

  // Base fluid colour — warm golden amber
  vec3 fluidA = vec3(0.435, 0.627, 0.937);  // #6fa0ef
  vec3 fluidB = vec3(0.176, 0.635, 0.898);  // #2da2e5
  vec3 fluidC = vec3(0.192, 0.384, 0.863);  // #3162dc
  vec3 fluid  = mix(fluidA, fluidB, pattern);
  fluid = mix(fluid, fluidC, smoothstep(0.4, 0.7, n2));

  // Specular-ish highlights on the fluid surface
  float highlight = smoothstep(0.62, 0.72, pattern) * 0.15;
  fluid += highlight;

  // Darker creases
  float crease = smoothstep(0.28, 0.22, pattern) * 0.25;
  fluid -= crease;

  // Edge darkening where fluid meets clear zone — gives a meniscus look
  float edgeDark = smoothstep(0.6, 0.0, abs(edge - 0.5)) * 0.38;
  fluid -= edgeDark;

  // Refraction-like distortion at the boundary
  float edgeHighlight = smoothstep(0.42, 0.5, edge) * smoothstep(0.58, 0.5, edge) * 0.4;
  fluid += edgeHighlight;

  // Slight translucency near edges — let background bleed a tiny bit
  float alpha = mix(0.98, 0.0, edge);

  // Add subtle animated caustic shimmer
  float caustic = smoothstep(0.68, 0.75, sin(pattern * 12.0 + u_time * 0.5) * 0.5 + 0.5) * 0.12;
  fluid += caustic * (1.0 - edge);

  gl_FragColor = vec4(fluid, alpha);
}
`;

const MAX_TRAIL = 64;

export default function About3D() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const glRef = useRef(null);
  const programRef = useRef(null);
  const rafRef = useRef(0);
  const trailRef = useRef([]);         // { x, y, size, strength, age }
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const lastPosRef = useRef({ x: -9999, y: -9999 });
  const timeRef = useRef(0);
  const startRef = useRef(null);

  const initGL = useCallback((canvas) => {
    const gl = canvas.getContext("webgl", {
      alpha: true,
      premultipliedAlpha: false,
      antialias: false,
    });
    if (!gl) return null;

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(s));
        return null;
      }
      return s;
    };

    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = compile(gl.FRAGMENT_SHADER, FRAG);
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(prog));
      return null;
    }
    gl.useProgram(prog);

    // Fullscreen quad
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1, 1, -1, -1, 1,
      -1, 1, 1, -1, 1, 1,
    ]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "a_position");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    glRef.current = gl;
    programRef.current = prog;
    return gl;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const gl = initGL(canvas);
    if (!gl) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    window.addEventListener("resize", resize);

    const prog = programRef.current;
    const loc = (name) => gl.getUniformLocation(prog, name);
    const u_resolution = loc("u_resolution");
    const u_time = loc("u_time");
    const u_count = loc("u_count");
    const u_dpr = loc("u_dpr");
    const u_points = [];
    const u_sizes = [];
    const u_strengths = [];
    for (let i = 0; i < MAX_TRAIL; i++) {
      u_points.push(loc(`u_points[${i}]`));
      u_sizes.push(loc(`u_sizes[${i}]`));
      u_strengths.push(loc(`u_strengths[${i}]`));
    }

    startRef.current = performance.now();

    const addTrailPoint = (x, y) => {
      const trail = trailRef.current;
      // Compute speed-based size: faster → bigger clear zone
      const dx = x - lastPosRef.current.x;
      const dy = y - lastPosRef.current.y;
      const speed = Math.sqrt(dx * dx + dy * dy);
      const size = Math.min(9 + speed * 0.9, 90);

      trail.unshift({ x, y, size, strength: 1.0, age: 0 });

      // Also spawn small satellite droplets around cursor
      if (speed > 3) {
        const angle = Math.atan2(dy, dx);
        for (let i = 0; i < 2; i++) {
          const spread = (Math.random() - 0.5) * 2.5;
          const dist = 30 + Math.random() * 50;
          trail.unshift({
            x: x + Math.cos(angle + spread) * dist,
            y: y + Math.sin(angle + spread) * dist,
            size: 25 + Math.random() * 30,
            strength: 0.6 + Math.random() * 0.3,
            age: 0,
          });
        }
      }

      if (trail.length > MAX_TRAIL) trail.length = MAX_TRAIL;
      lastPosRef.current = { x, y };
    };

    const handleMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      mouseRef.current = { x, y, active: true };
      addTrailPoint(x, y);
    };

    const handleEnter = (e) => {
      const rect = wrap.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      mouseRef.current = { x, y, active: true };
      lastPosRef.current = { x, y };
      addTrailPoint(x, y);
    };

    const handleLeave = () => {
      mouseRef.current.active = false;
    };

    wrap.addEventListener("pointermove", handleMove);
    wrap.addEventListener("pointerenter", handleEnter);
    wrap.addEventListener("pointerleave", handleLeave);

    const render = () => {
      const now = performance.now();
      timeRef.current = (now - startRef.current) / 1000;

      // Age and decay trail points
      const trail = trailRef.current;
      for (let i = trail.length - 1; i >= 0; i--) {
        trail[i].age += 0.016;
        // Strength decays over ~2.5 seconds
        trail[i].strength *= 0.985;
        // Sizes of nearby points merge: grow slightly when neighbors are close
        if (i > 0) {
          const dx = trail[i].x - trail[i - 1].x;
          const dy = trail[i].y - trail[i - 1].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < trail[i].size + trail[i - 1].size) {
            // Merge: grow both slightly, contributing to bigger blob
            const merge = 0.003;
            trail[i].size += merge;
            trail[i - 1].size += merge;
          }
        }
        if (trail[i].strength < 0.01) {
          trail.splice(i, 1);
        }
      }

      const rect = wrap.getBoundingClientRect();
      gl.uniform2f(u_resolution, rect.width * dpr, rect.height * dpr);
      gl.uniform1f(u_time, timeRef.current);
      gl.uniform1i(u_count, trail.length);
      gl.uniform1f(u_dpr, dpr);

      for (let i = 0; i < MAX_TRAIL; i++) {
        if (i < trail.length) {
          const p = trail[i];
          // Flip Y for WebGL (origin bottom-left)
          gl.uniform2f(u_points[i], p.x, rect.height - p.y);
          gl.uniform1f(u_sizes[i], p.size);
          gl.uniform1f(u_strengths[i], p.strength);
        } else {
          gl.uniform2f(u_points[i], -9999, -9999);
          gl.uniform1f(u_sizes[i], 0);
          gl.uniform1f(u_strengths[i], 0);
        }
      }

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      wrap.removeEventListener("pointermove", handleMove);
      wrap.removeEventListener("pointerenter", handleEnter);
      wrap.removeEventListener("pointerleave", handleLeave);
    };
  }, [initGL]);

  return (
    <section className="about3d-section" data-section="about">
      <div className="about3d-card" ref={wrapRef}>
        {/* WebGL fluid overlay canvas */}
        <canvas
          ref={canvasRef}
          className="about3d-fluid-canvas"
        />

        <div className="about3d-content">
          <div className="about3d-left">
            <p className="about3d-kicker">Hover to reveal</p>

            <h2 className="about3d-title">I am Ankith Rudra.</h2>

            <p className="about3d-copy">
              I build intelligent systems and interactive experiences across AI,
              backend engineering, distributed systems, and creative interfaces.
            </p>

            <p className="about3d-copy">
              I like making things that are fast, smart, and actually enjoyable
              to use.
            </p>

            <p className="about3d-copy">
              Especially passionate about AI/ML, LLMs, and computer vision.
            </p>
          </div>

          <div className="about3d-divider" />

          <div className="about3d-right">
            <div className="about3d-linkblock">
              <h3>PROJECTS / GAMES</h3>
              <p>
                Interactive projects, games.
                <br />
                ASMR game below.
              </p>
            </div>

            <div className="about3d-linkblock">
              <h3>SWITCH TO 2D</h3>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
